import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import CommercialProductsConfig from "../components/CommercialProductsConfig";
import PaymentConditionsConfig from "../components/PaymentConditionsConfig";
import EconomicParametersConfig from "../components/EconomicParametersConfig";
import UsersConfig from "../components/UsersConfig";
import AgendaAvailabilityConfig from "../components/AgendaAvailabilityConfig";
import ProfessionalsConfig from "../components/ProfessionalsConfig";

type Service = {
  id: string | number;
  code: string;
  name: string;
  family: string | null;
  active: boolean;
};

export default function Configuration() {
  const { show } = useToast();
  const { hasPermission } = useAuth();
  const canManageAgenda =
    hasPermission("agenda.blocks.manage");
  const canViewProfessionals =
    hasPermission("professionals.view");

  const [activeTab, setActiveTab] = useState<
    | "services"
    | "agenda"
    | "products"
    | "payments"
    | "economics"
    | "professionals"
    | "users"
  >("services");

  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [family, setFamily] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      setServices(await api.getServices());
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar los servicios.", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  function openNew() {
    setEditing(null);
    setCode("");
    setName("");
    setFamily("");
    setShowForm(true);
  }

  function openEdit(service: Service) {
    setEditing(service);
    setCode(service.code);
    setName(service.name);
    setFamily(service.family || "");
    setShowForm(true);
  }

  function closeForm() {
    if (submitting) return;
    setShowForm(false);
    setEditing(null);
  }

  async function submit(e: any) {
    e.preventDefault();

    if (!name.trim() || (!editing && !code.trim())) {
      show("Completá nombre y código.", "error");
      return;
    }

    setSubmitting(true);
    try {
      if (editing) {
        await api.updateService(editing.id, {
          name: name.trim(),
          family: family.trim() || null,
        });
        show("Servicio actualizado.", "success");
      } else {
        await api.createService({
          code: code.trim().toLowerCase(),
          name: name.trim(),
          family: family.trim() || null,
        });
        show("Servicio creado.", "success");
      }

      setShowForm(false);
      setEditing(null);
      await loadServices();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo guardar el servicio.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(service: Service) {
    try {
      await api.updateService(service.id, { active: !service.active });
      show(
        service.active ? "Servicio desactivado." : "Servicio activado.",
        "success"
      );
      await loadServices();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo actualizar el servicio.", "error");
    }
  }

  return (
    <div className="leads-page">
      <div className="kanban-header">
        <div>
          <h1>Configuración</h1>
          <p className="meta">
            Administración operativa, económica, usuarios y permisos del CRM.
          </p>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className={activeTab === "services" ? "btn-primary" : "btn-ghost"}
          onClick={() => setActiveTab("services")}
        >
          Servicios y tratamientos
        </button>

        <button
          type="button"
          className={activeTab === "products" ? "btn-primary" : "btn-ghost"}
          onClick={() => setActiveTab("products")}
        >
          Productos comerciales
        </button>

        <button
          type="button"
          className={activeTab === "payments" ? "btn-primary" : "btn-ghost"}
          onClick={() => setActiveTab("payments")}
        >
          Condiciones de cobro
        </button>

        <button
          type="button"
          className={activeTab === "economics" ? "btn-primary" : "btn-ghost"}
          onClick={() => setActiveTab("economics")}
        >
          Parámetros económicos
        </button>

        {canManageAgenda && (
          <button
            type="button"
            className={
              activeTab === "agenda"
                ? "btn-primary"
                : "btn-ghost"
            }
            onClick={() => setActiveTab("agenda")}
          >
            Agenda y disponibilidad
          </button>
        )}
        {canViewProfessionals && (
          <button
            type="button"
            className={
              activeTab === "professionals"
                ? "btn-primary"
                : "btn-ghost"
            }
            onClick={() =>
              setActiveTab("professionals")
            }
          >
            Profesionales
          </button>
        )}

        <button
          type="button"
          className={activeTab === "users" ? "btn-primary" : "btn-ghost"}
          onClick={() => setActiveTab("users")}
        >
          Usuarios y permisos
        </button>
      </div>

      {activeTab === "services" && (
        <>
          <div className="kanban-header">
            <div>
              <h2>Servicios y tratamientos</h2>
              <p className="meta">
                Catálogo clínico y comercial de Mauvelore.
              </p>
            </div>

            <button className="btn-primary" onClick={openNew}>
              + Nuevo servicio
            </button>
          </div>

          {loading && <div className="loading-strip">Cargando...</div>}

          <div className="contact-list">
        {services.map((service) => (
          <div key={service.id} className="contact-row">
            <div
              style={{ flex: 1, cursor: "pointer" }}
              onClick={() => openEdit(service)}
            >
              <div className="name">
                {service.name}
                {!service.active && " · Inactivo"}
              </div>

              <div className="meta">
                Código: {service.code}
                {service.family ? ` · Familia: ${service.family}` : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => openEdit(service)}
              >
                Editar
              </button>

              <button
                type="button"
                className="btn-ghost"
                onClick={() => toggleActive(service)}
              >
                {service.active ? "Desactivar" : "Activar"}
              </button>
            </div>
          </div>
        ))}

        {!loading && services.length === 0 && (
          <p className="meta">
            No hay servicios configurados. Creá el primero con “Nuevo servicio”.
          </p>
        )}
          </div>
        </>
      )}

      {activeTab === "agenda" && canManageAgenda && (
        <AgendaAvailabilityConfig />
      )}

      {activeTab === "products" && <CommercialProductsConfig />}

      {activeTab === "payments" && <PaymentConditionsConfig />}

      {activeTab === "economics" && <EconomicParametersConfig />}

      {activeTab === "professionals" &&
        canViewProfessionals && (
          <ProfessionalsConfig />
        )}

      {activeTab === "users" && <UsersConfig />}

      {showForm && (
        <div className="modal-backdrop" onClick={closeForm}>
          <div
            className="modal modal-wide"
            onClick={(e: any) => e.stopPropagation()}
          >
            <h3>{editing ? "Editar servicio" : "Nuevo servicio"}</h3>

            <form onSubmit={submit} className="form-grid">
              <label>Nombre</label>
              <input
                value={name}
                onChange={(e: any) => setName(e.target.value)}
                placeholder="Ej: Endolifting"
                required
              />

              <label>Código</label>
              <input
                value={code}
                onChange={(e: any) => setCode(e.target.value)}
                placeholder="Ej: endolifting"
                disabled={Boolean(editing)}
                required
              />

              {editing && (
                <>
                  <span />
                  <p className="meta">
                    El código queda fijo porque identifica al servicio dentro del CRM.
                  </p>
                </>
              )}

              <label>Familia / categoría</label>
              <input
                value={family}
                onChange={(e: any) => setFamily(e.target.value)}
                placeholder="Ej: facial, corporal, wellness"
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={closeForm}
                  disabled={submitting}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
